import { Button } from '@/components/ui-kit/button';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui-kit/sheet';

import { cn } from '@/lib/utils';
import { type UserRole } from '@/state/store/auth/index.type';
import {
  BarChart3,
  FileText,
  LayoutDashboard,
  PanelLeftClose,
  Settings,
  Users,
  X,
} from 'lucide-react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

const allNavItems = [
  {
    href: '/admin',
    labelKey: 'ADMIN_SIDEBAR_NAV_DASHBOARD',
    icon: LayoutDashboard,
    roles: ['sys_admin'] as UserRole[],
  },
  {
    href: '/admin/templates',
    labelKey: 'ADMIN_SIDEBAR_NAV_TEMPLATES',
    icon: FileText,
    roles: ['sys_admin', 'sys_editor'] as UserRole[],
  },
  {
    href: '/admin/analytics',
    labelKey: 'ADMIN_SIDEBAR_NAV_ANALYTICS',
    icon: BarChart3,
    roles: ['sys_admin', 'sys_editor'] as UserRole[],
  },
  {
    href: '/admin/users',
    labelKey: 'ADMIN_SIDEBAR_NAV_USERS',
    icon: Users,
    roles: ['sys_admin'] as UserRole[],
  },
  {
    href: '/admin/settings',
    labelKey: 'ADMIN_SIDEBAR_NAV_SETTINGS',
    icon: Settings,
    roles: ['sys_admin'] as UserRole[],
  },
];

interface AdminSidebarProps {
  userRole: UserRole;
  /** When provided, renders as a Radix Dialog–backed mobile drawer (Sheet). */
  isMobile?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AdminSidebar({
  userRole,
  isMobile = false,
  open,
  onOpenChange,
}: AdminSidebarProps) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const close = () => onOpenChange?.(false);

  // Filter nav items based on user role
  const navItems = allNavItems.filter((item) => item.roles.includes(userRole));

  const isActive = (href: string) => {
    if (href === '/admin') {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  const sidebarContent = (
    <div
      className={cn(
        'flex flex-col h-full bg-[hsl(var(--bg))] border-r border-border',
        isMobile ? 'w-80' : collapsed ? 'w-16' : 'w-64',
        'transition-all duration-300'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-border">
        {!collapsed && !isMobile && (
          <Link
            to={`/admin${userRole === 'sys_editor' ? '/templates' : ''}`}
            className="text-lg font-semibold text-selise-blue font-heading"
          >
            {t('ADMIN_SIDEBAR_TITLE')}
          </Link>
        )}
        {isMobile && (
          <>
            <Link
              to={`/admin${userRole === 'sys_editor' ? '/templates' : ''}`}
              onClick={close}
              className="text-lg font-semibold text-selise-blue font-heading truncate min-w-0"
            >
              {t('ADMIN_SIDEBAR_TITLE')}
            </Link>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close sidebar"
                className="h-9 w-9 shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </SheetClose>
          </>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8"
          >
            <PanelLeftClose
              className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
            />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={isMobile ? close : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                active
                  ? 'text-selise-blue bg-selise-blue/10'
                  : 'text-globe-grey hover:text-selise-blue hover:bg-muted',
                collapsed && !isMobile && 'justify-center'
              )}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {(!collapsed || isMobile) && <span>{t(item.labelKey)}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="left"
          hideClose
          className="w-80 max-w-[85vw] p-0 sm:max-w-sm md:hidden"
        >
          {/* SheetTitle is required by Radix for a11y; visually hidden since
              the in-content header already shows the brand title. */}
          <SheetTitle className="sr-only">{t('ADMIN_SIDEBAR_TITLE')}</SheetTitle>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return sidebarContent;
}
